import React, { useState, useEffect } from 'react';
import { PokemonCard } from '../../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { Search, ArrowLeft } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { supabase } from '../../lib/supabase';

interface SetInfo {
  name: string;
  display_name: string;
  display_name_en?: string;
}

interface WantedCardSummaryProps {
  card: PokemonCard;
  className?: string;
  onChangeCard: () => void;
  showChangeButton?: boolean;
}

export const WantedCardSummary = ({ 
  card, 
  className = '', 
  onChangeCard,
  showChangeButton = true
}: WantedCardSummaryProps) => {
  const { t, lang } = useTranslation();
  const [setInfo, setSetInfo] = useState<SetInfo | null>(null);
  
  useEffect(() => {
    const fetchSetInfo = async () => {
      if (!card.set) return;
      
      try {
        const { data, error } = await supabase
          .from('sets')
          .select('name, display_name, display_name_en')
          .eq('name', card.set)
          .single();
          
        if (error) throw error;
        setSetInfo(data);
      } catch (error) {
        console.error('Erreur lors du chargement des informations du set:', error);
      }
    };
    
    fetchSetInfo();
  }, [card.set]);
  
  const getCardName = (): string => {
    if (lang === 'en') {
      return card.card_en || card.card_fr || '';
    }
    return card.card_fr || card.card_en || '';
  };
  
  const getSetDisplayName = (): string => {
    if (!setInfo) return card.set || '';
    
    return lang === 'en' 
      ? (setInfo.display_name_en || setInfo.display_name) 
      : setInfo.display_name;
  };
  
  const getImageUrl = (): string => {
    const basePath = `${SUPABASE_IMAGE_BASE_URL}`;
    const imagePath = card.image || '';
    
    if (lang === 'en') {
      return `${basePath}en/${imagePath}`;
    }
    
    return `${basePath}${imagePath}`;
  };

  return (
    <div className={`flex items-center justify-between gap-4 bg-gradient-to-r from-blue-50 to-white rounded-lg p-3 sm:p-4 border border-blue-100 ${className}`}>
      <div className="flex items-center gap-4">
        <div className="shrink-0">
          <img
            src={getImageUrl()}
            alt={getCardName()}
            className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Search className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium text-blue-600">{t('create_trade.wanted_card')}</span>
          </div>
          <h4 className="font-medium text-gray-900 truncate">{getCardName()}</h4>
          <p className="text-sm text-gray-500">
            {getSetDisplayName()} • {card.rarity}
          </p>
        </div>
      </div>

      {showChangeButton && (
        <button
          onClick={onChangeCard}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('create_trade.change')}
        </button>
      )}
    </div>
  );
}; 